import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FooterButtons } from '../../components/styles'
import StyledModal from '../../components/CustomModal'
import { Form, Input } from 'antd'
import { AddButton } from '../../pages/Category/styles';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import { message, Upload, Button } from 'antd';
import { CustomRender, DeleteOutlinedIcon, FileLabel, FormDivider, FormItem, FormItemLabel, FormWrapperContainer, UploadedFilesLabel, UploadInfoText } from '../styles';
import ConfirmationModal from '../../components/ConfirmationModal';
import { CustomUploadFile } from '../../utils/helpers/validateUploadFiles';
import { useAppDispatch, useAppSelector } from '../../utils/hooks/redux';
import { updateTrackingNumber, uploadAdditionalDoc, uploadShippingLabel } from '../../redux/features/report/reportAsyncThunk';
import { handleDownloadDocument } from '../../utils/helpers/downloadDocument';
import { selectUserRole } from '../../redux/features/auth/selectors';
import { checkHasPermission } from '../../utils/helpers/checkHasPermission';
import { permissions } from '../../utils/constants/permissions';
import UploadDocsModal from '../UploadDocs';
import TrackingNumber from '../TrackingNumber';
import { toast } from 'react-toastify';

interface ShippingModalProps {
    isMarkModalOpen: boolean;
    handleMarkCancel: () => void;
    handleSaveSuccess: () => void;
}

const ShippingModal: React.FC<ShippingModalProps> = ({ isMarkModalOpen, handleMarkCancel, handleSaveSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [showDeleteConfirmItem, setShowDeleteConfirmItem] = useState<UploadFile | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const { report } = useAppSelector((state) => state.report);
    const dispatch = useAppDispatch();

    const userRole = useAppSelector(selectUserRole);

    const hasPermission = useCallback(
        (permissionKey: string) => checkHasPermission(userRole, permissionKey),
        [userRole]
    );

    const isAddPermission = useMemo(() => hasPermission(permissions.AddEditTrackingDocs), [hasPermission]);
    const isEditPermission = useMemo(() => hasPermission(permissions.AddEditTrackingDocs), [hasPermission]);
    const isDeletePermission = useMemo(() => hasPermission(permissions.DeleteTrackingDocs), [hasPermission]);

    const handleFileChange = async ({ fileList: newFileList }: { fileList: CustomUploadFile[] }) => {
        if (report?.id && report?.shippingLabel?.id) {
            message.error('Please remove the existing shipping label before uploading a new one.');
            return;
        }

        setFileList(newFileList.slice(0, 1));
        const blobs = newFileList
            .map((file: UploadFile) => file.originFileObj ? new Blob([file.originFileObj], { type: file.originFileObj.type }) : null)
            .filter((blob) => blob !== null);

        form.setFieldsValue({ file: blobs });
    };

    const handleRemove = async (file: CustomUploadFile) => {
        const documentId = report?.shippingLabel?.id?.toString() === file?.uid;
        if (documentId && file?.uid && report?.id?.length) {
            try {
                if (report?.id) {
                    setLoading(true);
                    const formData = new FormData();
                    formData.append('deletedFile', file?.uid);
                    await dispatch(uploadShippingLabel({ lostId: report?.id, formdata: formData, isDelete: true }));
                    form.resetFields(['file']);
                    setFileList([]);
                    setShowDeleteConfirmItem(null);
                    message.success(`${file.name} has been deleted.`);
                }
            } catch (error) {
                console.log({ error })
            } finally {
                setLoading(false);
            }
        } else {
            form.resetFields();
            setFileList([]);
            setShowDeleteConfirmItem(null);
        }
    };


    const beforeUpload = (file: any) => {
        const isValidFileType =
            file.type === 'application/pdf' ||
            file.type === 'image/jpeg' ||
            file.type === 'image/jpg' ||
            file.type === 'image/png' ||
            file.type === 'image/webp';

        if (!isValidFileType) {
            message.error('You can only upload PDF, JPEG, JPG, PNG, or WEBP files!');
        }
        return false;
    };

    const props: UploadProps = {
        name: 'file',
        multiple: false,
        disabled: !(isAddPermission || isEditPermission),
        accept: '.pdf',
        beforeUpload,
        fileList: fileList,
        listType: "text",
        maxCount: 1,
        showUploadList: {
            showPreviewIcon: true,
            showRemoveIcon: true,
            showDownloadIcon: false,
        },
        onChange: handleFileChange,
        onDrop(e) {
            e.preventDefault();
            const droppedFiles = Array.from(e.dataTransfer.files).map((file) => ({
                uid: file.name, // Or use a unique ID generator
                name: file.name,
                status: 'done',
                originFileObj: file,
            })) as UploadFile[];

            handleFileChange({ fileList: droppedFiles });
        },
        onRemove: handleRemove,
        itemRender: (originNode, file) => (
            <>
                {/* Show "Uploaded files" label only once, for the first file */}
                {fileList?.[0]?.uid === file.uid && (
                    <UploadedFilesLabel>Uploaded files</UploadedFilesLabel>
                )}

                <CustomRender align="center" justify='space-between'>
                    <FileLabel>{originNode}</FileLabel>
                    {isDeletePermission && <Button
                        type="text"
                        icon={<DeleteOutlinedIcon />}
                        onClick={() => setShowDeleteConfirmItem(file)}
                    />}
                </CustomRender>
            </>
        ),
    };

    const handleFormSubmit = async () => {
        try {
            if (report?.id) {
                setLoading(true);
                const values = await form.getFieldsValue();
                let file = [];
                let response: any = {};
                if (Array.isArray(values?.file)) {
                    await form.validateFields(['file']);
                    const hasBlob = values.file.some((doc: any) => doc instanceof Blob);
                    if (hasBlob) {
                        file = values?.file;
                    }
                }
                const formattedValues = {
                    ...values,
                    file: file?.[0],
                }

                if (formattedValues?.file) {
                    response = await dispatch(uploadShippingLabel({ lostId: report?.id, formdata: formattedValues, isDelete: false })).unwrap();
                }
                if (!values?.trackingNumber) {
                    await form.validateFields(['trackingNumber']);
                }
                if (values?.trackingNumber && values?.trackingNumber !== report?.trackingNumber) {
                    response = await dispatch(updateTrackingNumber({ lostId: report?.id, trackingNumber: values.trackingNumber })).unwrap();
                }
                let files = [];
                if (Array.isArray(values?.files)) {
                    await form.validateFields(['files']);
                    const hasBlob = values.files.some((doc: any) => doc instanceof Blob);
                    if (hasBlob) {
                        files = values?.files;
                    }
                }
                const formattedFilesValues = {
                    ...values,
                    files: files,
                }
                if (formattedFilesValues?.files?.length > 0) {
                    response = await dispatch(uploadAdditionalDoc({ lostId: report?.id, formdata: formattedFilesValues, isDelete: false })).unwrap();
                }

                if (response?.documents || response?.id) {
                    toast.success("Shipping Label/ Additional Docs/ Courier Tracking Info saved successfully");
                }
                form.resetFields();
                setFileList([]);
                handleSaveSuccess();
            }
            // else if (report?.id && report?.shippingLabel?.id) {
            //     setLoading(true);
            //     await handleDownloadDocument(report?.id, report?.shippingLabel, `ShippingLabel_${report?.trackingId}.pdf`)
            // }
        } catch (error) {
            console.log({ error })
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (report?.id && report?.shippingLabel && report?.shippingLabel?.id) {
            setFileList([{ uid: report?.shippingLabel?.id.toString(), name: report?.shippingLabel?.documentName, status: 'done', url: report?.shippingLabel?.fullUrl }])
        }
    }, [report])

    return (
        <>
            <StyledModal
                draggable={true}
                width={715}
                title={report?.shippingLabel?.id ? "Download Shipping Label/ Additional Docs/ Add Courier Tracking Info" : "Upload Shipping Label/ Additional Docs/ Add Courier Tracking Info"}
                centered
                open={isMarkModalOpen}
                onCancel={handleMarkCancel}
                footer={
                    <FooterButtons>
                        <AddButton shape='round' disabled={loading} onClick={handleMarkCancel}>Cancel</AddButton>
                        {(isAddPermission || isEditPermission) && <AddButton shape='round' loading={loading} disabled={loading} onClick={handleFormSubmit} type="primary">
                            {report?.shippingLabel?.id ? "Update" : "Save"}
                        </AddButton>}
                    </FooterButtons>
                }
                closeIcon={null}
            >
                <FormWrapperContainer disabled={loading} form={form} name="shipping" layout="vertical">
                    <Form.Item name="deletedFile" hidden>
                        <Input />
                    </Form.Item>
                    <FormItem label={<FormItemLabel>Uploaded Shipping Label</FormItemLabel>} name="file" rules={[{ required: true, message: "Please upload shipping label." }]}>
                        {(isAddPermission || isEditPermission) && <UploadInfoText color='#FF4D4F'>You must upload the shipping label in order to make it ready to ship. Prefer PDF</UploadInfoText>}
                        <Upload.Dragger className='shipping_upload_wrapper' {...props}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Click or drag file to this area to upload shipping label</p>
                        </Upload.Dragger>
                    </FormItem>
                </FormWrapperContainer>
                <FormDivider />
                <TrackingNumber form={form} loading={loading} />
                <FormDivider />
                <UploadDocsModal form={form} isLoading={loading} />
            </StyledModal>

            {showDeleteConfirmItem && (
                <ConfirmationModal
                    handleSave={() => { handleRemove(showDeleteConfirmItem); setFileList([]); }}
                    isModalOpen={!!showDeleteConfirmItem}
                    onCancel={() => setShowDeleteConfirmItem(null)}
                    title={`Confirm Delete?`}
                    message={`Are you sure you want to delete the ${showDeleteConfirmItem.name}?`}
                    confirmButtonText="Delete"
                    loading={loading}
                />
            )}

        </>
    )
}

export default ShippingModal;
